import type { MatrixSession } from '$lib/types';
import { AFTER_LOGIN_REDIRECT_KEY, SESSION_KEYS } from '$lib/constants';
import { StorageService } from './storage.service';

export class SessionService {
	/**
	 * Stores the session under Cinny's legacy `cinny_*` keys.
	 * @param accessToken - Matrix access token
	 * @param deviceId - device id of this login
	 * @param userId - full Matrix user id
	 * @param baseUrl - homeserver base URL
	 */
	static setFallback(accessToken: string, deviceId: string, userId: string, baseUrl: string): void {
		StorageService.setItem(SESSION_KEYS.ACCESS_TOKEN, accessToken);
		StorageService.setItem(SESSION_KEYS.DEVICE_ID, deviceId);
		StorageService.setItem(SESSION_KEYS.USER_ID, userId);
		StorageService.setItem(SESSION_KEYS.BASE_URL, baseUrl);
	}

	/** Deletes the stored session. */
	static removeFallback(): void {
		StorageService.remove(SESSION_KEYS.BASE_URL);
		StorageService.remove(SESSION_KEYS.USER_ID);
		StorageService.remove(SESSION_KEYS.DEVICE_ID);
		StorageService.remove(SESSION_KEYS.ACCESS_TOKEN);
	}

	/** @returns the stored session when all four keys are present */
	static getFallback(): MatrixSession | undefined {
		const baseUrl = StorageService.getItem(SESSION_KEYS.BASE_URL);
		const userId = StorageService.getItem(SESSION_KEYS.USER_ID);
		const deviceId = StorageService.getItem(SESSION_KEYS.DEVICE_ID);
		const accessToken = StorageService.getItem(SESSION_KEYS.ACCESS_TOKEN);

		if (baseUrl && userId && deviceId && accessToken) {
			return { baseUrl, userId, deviceId, accessToken, fallbackSdkStores: true };
		}
		return undefined;
	}

	/** @param url - app path to return to once login completes */
	static setAfterLoginRedirect(url: string): void {
		StorageService.setItem(AFTER_LOGIN_REDIRECT_KEY, url);
	}

	/** @returns the remembered post-login app path */
	static getAfterLoginRedirect(): string | undefined {
		return StorageService.getItem(AFTER_LOGIN_REDIRECT_KEY) ?? undefined;
	}

	/** Forgets the remembered post-login app path. */
	static deleteAfterLoginRedirect(): void {
		StorageService.remove(AFTER_LOGIN_REDIRECT_KEY);
	}
}
